import { type ComponentProps, createContext, useContext } from "react";
import { tv } from "tailwind-variants";

import type { SubscriptionDataModel } from "@/domains/careplan/models/types";
import { Heading, type HeadingProps } from "@/ui";

const subscriptionStatusVariants = tv({
  slots: {
    root: "rounded-xl bg-canvas p-5",
    description: "text-sm text-content-secondary",
    shippingFrequency: "text-sm whitespace-nowrap text-content-secondary",
    nextBilling: "text-sm text-content",
  },
});

const { description, nextBilling, root, shippingFrequency } = subscriptionStatusVariants();

type SubscriptionStatusCardProps = {
  subscription: SubscriptionDataModel;
} & ComponentProps<"div">;

const SubscriptionStatusCardContext = createContext<SubscriptionDataModel | null>(null);

export const useSubscriptionStatusCardContext = () => {
  const subscription = useContext(SubscriptionStatusCardContext);
  if (!subscription) {
    throw Error(
      "useSubscriptionStatusCardContext can only be used inside a SubscriptionStatusCardContext.Provider",
    );
  }

  return subscription;
};

const Root = ({ children, className, subscription }: SubscriptionStatusCardProps) => {
  return (
    <SubscriptionStatusCardContext.Provider value={subscription}>
      <div className={root({ className })}>{children}</div>
    </SubscriptionStatusCardContext.Provider>
  );
};

const Title = ({ children, ...props }: HeadingProps) => {
  return <Heading {...props}>{children}</Heading>;
};

const Description = ({ children, className, ...props }: ComponentProps<"p">) => {
  return (
    <p className={description({ className })} {...props}>
      {children}
    </p>
  );
};

const ShippingFrequency = ({ className, ...props }: Omit<ComponentProps<"p">, "children">) => {
  return (
    <p className={shippingFrequency({ className })} {...props}>
      Ships monthly
    </p>
  );
};

const NextBillingDate = ({ className, ...props }: Omit<ComponentProps<"p">, "children">) => {
  const { nextBillingDate } = useSubscriptionStatusCardContext();

  if (!nextBillingDate) {
    return null;
  }

  return (
    <p className={nextBilling({ className })} {...props}>
      <span className="font-semibold">Next Billing Date:</span> {nextBillingDate}
    </p>
  );
};

export const CareplanCartSubscriptionStatus = {
  Root,
  Title,
  Description,
  ShippingFrequency,
  NextBillingDate,
};
